import React, { useEffect, useState } from 'react';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { Box, CircularProgress } from '@material-ui/core';
import ee from '../../../services/earth-engine';

const thumbCache = {};
const errorCache = {};

/**
 * @function SatelliteThumbnail
 * @description A small preview of one image of a collection, looked up by its `system:index`, or of a ready URL.
 * The thumbnail URL is requested with a callback. Without one, the Earth Engine client sends the request synchronously, and a
 * table of images held the page frozen while each row serialised the collection and the area - seconds per row on a detailed
 * KML area, minutes for the table.
 * @param {Object} props
 * @param {String} [props.url] - A ready thumbnail URL; when given, nothing is requested.
 * @param {String} [props.id] - The image's `system:index` in `collectionRef.current`.
 * @param {Number} [props.height] - Height in pixels.
 * @param {Object} [props.roi] - The region of the thumbnail, as an `ee.Geometry`.
 * @param {Array<String>} [props.bands] - The three bands shown as red, green and blue.
 * @param {Object} [props.collectionRef] - Ref holding the `ee.ImageCollection` the id belongs to.
 * @memberof module:Shared
 * @returns {JSX.Element}
 */
const SatelliteThumbnail = ({
  url: propUrl = '',
  id = '',
  height = 80,
  roi = {},
  bands = ['B4', 'B3', 'B2'],
  collectionRef = [],
}) => {
  const [error, setError] = useState(false);
  const [eeUrl, setEeUrl] = useState('');
  const activeUrl = propUrl || eeUrl;

  useEffect(() => {
    if (propUrl || !id || !roi || !collectionRef?.current) return;
    if (errorCache[id]) return setError(true);
    if (thumbCache[id]) return setEeUrl(thumbCache[id]);
    const timeout = setTimeout(() => {
      try {
        const filtered = collectionRef.current.filter(ee.Filter.eq('system:index', id));
        filtered.size().evaluate((count) => {
          if (!count) {
            errorCache[id] = true;
            setError(true);
            return;
          }
          const image = ee.Image(filtered.first());
          image.getThumbURL(
            {
              region: roi,
              bands: bands,
              dimensions: 128,
              min: 0,
              max: 0.2,
              format: 'png',
            },
            (thumbUrl, thumbError) => {
              if (thumbError || !thumbUrl) {
                console.error('Thumbnail error:', id, thumbError);
                errorCache[id] = true;
                setError(true);
                return;
              }
              thumbCache[id] = thumbUrl;
              setEeUrl(thumbUrl);
            },
          );
        });
      } catch (e) {
        console.error('Thumbnail sync error:', e);
        errorCache[id] = true;
        setError(true);
      }
    }, 120);
    return () => clearTimeout(timeout);
  }, [id, propUrl, roi, bands]);

  return (
    <Box display="flex" alignItems="center" height={height}>
      {error ? (
        <Box
          style={{
            height,
            width: height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            color: '#999',
            border: '1px solid #eee',
          }}>
          N/A
        </Box>
      ) : activeUrl ? (
        <span style={{ display: 'inline-flex', lineHeight: 0 }} onClick={(e) => e.stopPropagation()}>
          <Zoom zoomMargin={50} overlayBgColorStart="rgba(255, 255, 255, 0)" overlayBgColorEnd="rgba(255, 255, 255, 0.5)">
            <img
              src={activeUrl}
              alt="Satellite Thumbnail"
              style={{ maxHeight: height, border: '1px solid #CCC', objectFit: 'cover' }}
              onError={() => {
                console.error('Thumbnail load error:', id);
                errorCache[id] = true;
                setError(true);
              }}
            />
          </Zoom>
        </span>
      ) : (
        <CircularProgress size={18} />
      )}
    </Box>
  );
};

export default SatelliteThumbnail;
